import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSettingsDatasourcesController } from '../state/useSettingsDatasourcesController'

const PLATFORM_ORDER = ['facebook', 'news', 'x', 'threads'] as const

function Toggle() {
  return (
    <div
      role="switch"
      aria-checked={false}
      aria-disabled
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-not-allowed items-center rounded-full',
        'bg-muted transition-colors',
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-3.5 w-3.5 translate-x-0.5 rounded-full',
          'bg-muted-foreground/50 shadow-sm transition-transform',
        )}
      />
    </div>
  )
}

export function SettingsDatasourcesTab() {
  const { t } = useTranslation('settings')
  const { grouped, isLoading } = useSettingsDatasourcesController()

  const allPlatforms = Array.from(
    new Set([
      ...PLATFORM_ORDER,
      ...Object.keys(grouped).filter((p) => !PLATFORM_ORDER.includes(p as never)),
    ]),
  ).filter((p) => grouped[p]?.length)

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((n) => (
          <div key={n} className="rounded-lg border border-border bg-card px-6 py-8">
            <div className="space-y-4">
              {[1, 2, 3].map((m) => (
                <div key={m} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card px-6 py-4">
        <h2 className="text-base font-semibold text-foreground">
          {t('datasources.title')}
        </h2>
      </div>

      {allPlatforms.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
          <p className="text-sm text-muted-foreground">{t('datasources.noSources')}</p>
        </div>
      )}

      {allPlatforms.map((platform) => {
        const sources = grouped[platform] ?? []
        const platformLabel =
          t(`datasources.platform.${platform}`, { defaultValue: '' }) || platform

        return (
          <div key={platform} className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-6 py-4">
              <h3 className="text-sm font-semibold text-foreground">{platformLabel}</h3>
            </div>

            <ul className="divide-y divide-border">
              {sources.map((source) => (
                <li key={source.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {source.name}
                    </p>
                    {source.url && (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {source.url}
                      </a>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    <Toggle />
                    <span className="text-xs text-muted-foreground">
                      {t('datasources.comingSoon')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
